// `OpeningHours` and `Holiday` — when a venue is open (spec/13 §13 "Venue profile", F-ORG-01).
//
// "Opening hours per weekday, holidays calendar." The shapes are the contract's to fix because
// three things read them and must agree to the minute: the venue console that edits them, the
// player app that shows "Open now" (spec/08 §4.3), and the server that warns running sessions
// before closing time (`venue.closingAction`, spec/05 §12). The arithmetic over them lives in
// `packages/domain` (`openIntervalsBetween`, `closingsBetween`); this file is only the shape.
//
// Three rules the shape carries, chosen to match `TimeWindow` wherever a venue owner would expect
// the two to behave alike (spec/06 §3.1):
//
//  1. An interval is **half-open**, `[opens, closes)`: open at `opens`, closed at `closes`.
//  2. `closes` earlier than `opens` **crosses midnight** and belongs to the day it opens on:
//     `fri: [{ opens: '18:00', closes: '02:00' }]` is Friday 18:00 to Saturday 02:00.
//  3. `closes` **equal** to `opens` is **24 hours** — the one place this differs from a
//     `TimeWindow`, where equal is zero length. A pass window that never fires is harmless; an
//     opening interval of zero length is a venue that opens and shuts in the same instant, which no
//     owner means. Gaming cafés that never close are common, and `00:00`–`00:00` on every day is
//     how they say so.
import { z } from "../zod.js";
import { LocalDateSchema, LocalTimeSchema } from "./scalars.js";
/** Split shifts are real (a lunch break, a late session); four a day is plenty and bounds the maths. */
export const MAX_OPENING_INTERVALS_PER_DAY = 4;
/** A year of holidays and then some. The calendar is a list of dates, not a recurrence rule. */
export const MAX_HOLIDAYS = 400;
export const OpeningIntervalSchema = z
    .object({
    opens: LocalTimeSchema,
    closes: LocalTimeSchema,
})
    .strict()
    .openapi('OpeningInterval', {
    description: "Open from `opens` until `closes`, in the venue's timezone. `closes` earlier than `opens` crosses " +
        'midnight and belongs to the day it opens on; `closes` equal to `opens` is 24 hours.',
});
const dayIntervals = z.array(OpeningIntervalSchema).max(MAX_OPENING_INTERVALS_PER_DAY);
export const OpeningHoursSchema = z
    .object({
    mon: dayIntervals,
    tue: dayIntervals,
    wed: dayIntervals,
    thu: dayIntervals,
    fri: dayIntervals,
    sat: dayIntervals,
    sun: dayIntervals,
})
    .strict()
    .openapi('OpeningHours', {
    description: 'The regular week. An empty day is closed all day.',
});
/** A week with nothing in it: the default for a venue still in `setup`. Closed until someone says otherwise. */
export const CLOSED_ALL_WEEK = { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] };
/**
 * A date in the holiday calendar, and what it does to the day's hours — one of three things:
 *
 *   `closed: true`                   shut all day (`hours` must be empty);
 *   `closed: false`, `hours: []`     the weekday's regular hours — the date still matters, because
 *                                    `holiday` in a pass or rate window matches it (spec/06 §3.1);
 *   `closed: false`, `hours: [...]`  special hours that replace the weekday's.
 *
 * Flat rather than a union on purpose: the TypeScript client and the C# station DTOs are generated
 * from this, and a flag plus a list is a shape every generator and every form handles the same way.
 */
export const HolidaySchema = z
    .object({
    date: LocalDateSchema,
    name: z.string().trim().min(1).max(80),
    closed: z.boolean(),
    hours: z.array(OpeningIntervalSchema).max(MAX_OPENING_INTERVALS_PER_DAY),
})
    .strict()
    .refine((holiday) => !holiday.closed || holiday.hours.length === 0, {
    message: 'A holiday the venue is closed on has no hours.',
    path: ['hours'],
})
    .openapi('Holiday', {
    description: "A date in the venue's holiday calendar: closed, regular hours (no `hours`), or special hours. `holiday` in a pass or rate window matches it whatever weekday it falls on.",
});
export const HolidaysSchema = z
    .array(HolidaySchema)
    .max(MAX_HOLIDAYS)
    .refine((holidays) => new Set(holidays.map((holiday) => holiday.date)).size === holidays.length, {
    message: 'Each date may appear in the holiday calendar once.',
})
    .openapi('Holidays');
//# sourceMappingURL=opening-hours.js.map